import { externalEvents } from "@closerplatform/closer-sdk";
import { AppPresence } from "@closerplatform/spinner-openapi";
import { WsEventListener } from "@fidget/common/services/eventsListener";
import { fileService } from "@fidget/common/services/files/fileService";
import { logger } from "@fidget/common/utils";
import { observe } from "mobx";
import { PluginService } from "../../plugins/pluginService";
import { ICurrentUserStore } from "./CurrentUserStore";

export class CurrentUserEventListener implements WsEventListener {
  constructor(private _currentUserFinder: () => ICurrentUserStore, public name: string) {
    // subscribe to observables
    observe<ICurrentUserStore, any>(_currentUserFinder(), "loggedIn", (change) => {
      // mind that values are MST nodes, not simple types
      const { oldValue, newValue } = change;
      // since the callback is executed synchronously, immediately after observable
      // change, wrapping it in try-catch will help distinguish any possible exceptions
      // thrown herein from eg. rejected promises in action, thus enabling the action to
      // finish running
      try {
        this.onLoggedInChange(oldValue.storedValue, newValue.storedValue);
      } catch (e) {
        logger.error({
          message: "Failed to execute CurrentUserEventListener.onLoggedInChange",
          error: e
        });
      }
    });
  }

  private onLoggedInChange = (
    wasLoggedIn: boolean | undefined,
    isLoggedIn: boolean | undefined
  ) => {
    const { profile } = this.currentUser;
    if (!wasLoggedIn && isLoggedIn && profile) {
      // connect services
      this.currentUser.spinnerService.connect(profile.orgId, profile.apiKey);
      this.currentUser.artichokeService.connect(profile.id, profile.apiKey);
      PluginService.getInstance().connect(profile.id, profile.orgId, profile.apiKey);
      fileService.setSpinnerService(this.currentUser.spinnerService);
      fileService.setApiKey(profile.apiKey);
    } else if (wasLoggedIn && !isLoggedIn) {
      // disconnect services
      this.currentUser.artichokeService.disconnect();
    }
  };

  get currentUser() {
    return this._currentUserFinder();
  }

  presenceUpdated(event: externalEvents.PresenceUpdated) {
    if (this.currentUser.id === event.userId) {
      this.currentUser.setPresence(event.presence as unknown as AppPresence, false, event.reason);
    }
  }

  agentGroupCreated(event: externalEvents.AgentGroupCreated) {
    if (event.advisers.includes(this.currentUser.id!)) {
      this.currentUser.profile?.addGroup(event.id);
    }
  }

  agentGroupUpdated(event: externalEvents.AgentGroupUpdated) {
    if (event.advisers.includes(this.currentUser.id!)) {
      this.currentUser.profile?.addGroup(event.id);
    } else {
      this.currentUser.profile?.removeGroup(event.id);
    }
  }

  agentGroupDeleted(event: externalEvents.AgentGroupDeleted) {
    this.currentUser.profile?.removeGroup(event.id);
  }

  adviserTagGroupsUpdated(event: externalEvents.AdviserTagGroupsUpdated) {
    this.currentUser.profile?.replaceTagGroups([...event.tagGroups]);
  }

  unauthorized() {
    this.currentUser.logout();
  }
}
